// src/screens/DealerBillingScreen.js
import React, { useState, useEffect } from 'react';
import { billingAPI } from '../services/api';

const DealerBillingScreen = () => {
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusRes, plansRes] = await Promise.all([
        billingAPI.getStatus(),
        billingAPI.getPlans(),
      ]);

      setBilling(statusRes.data);
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError(err.response?.data?.message || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan, frequency) => {
    try {
      setProcessingPlan(`${plan}-${frequency}`);
      const { data } = await billingAPI.createCheckoutSession(plan, frequency);

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout: ' + (err.response?.data?.message || err.message));
      setProcessingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await billingAPI.createPortalSession();

      // Redirect to Stripe Billing Portal
      window.location.href = data.url;
    } catch (err) {
      console.error('Portal error:', err);
      alert('Failed to open billing portal: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-accent mx-auto mb-4"></div>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary">
            Loading billing information...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-900 dark:text-red-100 font-medium">Error</p>
          <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          <button
            onClick={fetchBillingData}
            className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { subscription, trial, planLimits, usage, hasActiveSubscription } = billing || {};

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-app-text-light dark:text-app-text mb-6">
        Billing & Subscription
      </h1>

      {/* Trial Banner */}
      {trial?.isTrialing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎉</span>
            <div className="flex-1">
              {subscription?.trialEndsAt ? (
                <>
                  <p className="text-blue-900 dark:text-blue-100 font-medium">
                    Free Trial Active: <strong>{trial.daysRemaining} days</strong> remaining
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Trial expires: {new Date(subscription.trialEndsAt).toLocaleDateString()}
                  </p>
                </>
              ) : (
                <p className="text-blue-900 dark:text-blue-100 font-medium">Free Trial Active</p>
              )}
              {subscription?.trialEndsAt && trial.daysRemaining <= 3 && (
                <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mt-2">
                  ⚠️ Your trial is ending soon! Subscribe below to continue access.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Status */}
      <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-6 mb-6">
        <h2 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-4">
          Current Plan
        </h2>

        {subscription?.status === 'active' && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm font-medium">
                ✓ Active
              </span>
            </div>
            <div className="space-y-2 text-app-text-secondary-light dark:text-app-text-secondary">
              <p>
                <strong className="text-app-text-light dark:text-app-text">Plan:</strong>{' '}
                {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} ({subscription.billingFrequency})
              </p>
              <p>
                <strong className="text-app-text-light dark:text-app-text">Renews:</strong>{' '}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </p>
              {subscription.cancelAtPeriodEnd && (
                <p className="text-yellow-600 dark:text-yellow-400 font-medium">
                  ⚠️ Subscription will cancel at period end
                </p>
              )}
            </div>
          </div>
        )}

        {subscription?.status === 'past_due' && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-yellow-900 dark:text-yellow-100 font-medium flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              Payment Failed
            </p>
            <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-1">
              Your payment is past due. Please update your payment method to restore access.
            </p>
            <button
              onClick={handleManageBilling}
              className="mt-3 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
            >
              Update Payment Method
            </button>
          </div>
        )}

        {subscription?.status === 'canceled' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-900 dark:text-red-100 font-medium">
              Subscription Canceled
            </p>
            <p className="text-sm text-red-800 dark:text-red-200 mt-1">
              Your subscription was canceled on {new Date(subscription.canceledAt).toLocaleDateString()}
            </p>
            <p className="text-sm text-red-800 dark:text-red-200 mt-1">
              Subscribe below to restore access to dealer features.
            </p>
          </div>
        )}

        {trial?.expired && (
          <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
            <p className="text-gray-900 dark:text-gray-100">
              Your free trial ended on {new Date(subscription?.trialEndsAt).toLocaleDateString()}. Choose a plan below to continue.
            </p>
          </div>
        )}
      </div>

      {/* Usage Stats */}
      <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-6 mb-6">
        <h2 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-4">
          Usage & Limits
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-app-bg-light dark:bg-app-bg rounded-lg">
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-1">
              Vehicles
            </p>
            <p className="text-2xl font-bold text-app-text-light dark:text-app-text">
              {usage?.vehicles || 0}
            </p>
            <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
              / {planLimits?.maxVehicles === -1 ? '∞' : planLimits?.maxVehicles}
            </p>
            {usage?.vehicles >= planLimits?.maxVehicles && planLimits?.maxVehicles !== -1 && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                ⚠️ Limit reached
              </p>
            )}
          </div>

          <div className="text-center p-4 bg-app-bg-light dark:bg-app-bg rounded-lg">
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-1">
              Staff
            </p>
            <p className="text-2xl font-bold text-app-text-light dark:text-app-text">
              {usage?.staff || 0}
            </p>
            <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
              / {planLimits?.maxStaff === -1 ? '∞' : planLimits?.maxStaff}
            </p>
          </div>

          <div className="text-center p-4 bg-app-bg-light dark:bg-app-bg rounded-lg">
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-1">
              Analytics
            </p>
            <p className="text-3xl text-app-text-light dark:text-app-text">
              {planLimits?.hasAnalytics ? '✓' : '✗'}
            </p>
          </div>

          <div className="text-center p-4 bg-app-bg-light dark:bg-app-bg rounded-lg">
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-1">
              AI Tools
            </p>
            <p className="text-3xl text-app-text-light dark:text-app-text">
              {planLimits?.hasAI ? '✓' : '✗'}
            </p>
          </div>
        </div>
      </div>

      {/* Available Plans */}
      {(!hasActiveSubscription || trial?.isTrialing) && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-4">
            Choose Your Plan
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrentPlan = subscription?.plan === plan.id;

              return (
                <div
                  key={plan.id}
                  className={`bg-white dark:bg-app-surface rounded-lg shadow-sm border ${
                    isCurrentPlan
                      ? 'border-app-accent ring-2 ring-app-accent ring-opacity-50'
                      : 'border-app-border-light dark:border-app-border'
                  } p-6 relative`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="px-3 py-1 bg-app-accent text-white text-xs font-medium rounded-full">
                        Current Plan
                      </span>
                    </div>
                  )}

                  <h3 className="text-lg font-bold text-app-text-light dark:text-app-text mb-2">
                    {plan.name}
                  </h3>

                  <div className="mb-4">
                    <p className="text-3xl font-bold text-app-accent">
                      ${plan.pricing.monthly.amount / 100}
                      <span className="text-sm text-app-text-secondary-light dark:text-app-text-secondary font-normal">
                        /month
                      </span>
                    </p>
                    <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                      or ${plan.pricing.yearly.amount / 100}/year (save 15%)
                    </p>
                  </div>

                  <ul className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-6 space-y-2">
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      {plan.limits.maxVehicles === -1 ? 'Unlimited' : `Up to ${plan.limits.maxVehicles}`} vehicles
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      {plan.limits.maxStaff === -1 ? 'Unlimited' : `Up to ${plan.limits.maxStaff}`} staff accounts
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={plan.limits.hasAnalytics ? 'text-green-500' : 'text-gray-400'}>
                        {plan.limits.hasAnalytics ? '✓' : '✗'}
                      </span>
                      Advanced analytics
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={plan.limits.hasAI ? 'text-green-500' : 'text-gray-400'}>
                        {plan.limits.hasAI ? '✓' : '✗'}
                      </span>
                      AI-powered tools
                    </li>
                  </ul>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleSubscribe(plan.id, 'monthly')}
                      disabled={processingPlan === `${plan.id}-monthly`}
                      className="w-full px-4 py-2 bg-app-accent hover:bg-app-accent-hover text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingPlan === `${plan.id}-monthly` ? 'Processing...' : 'Subscribe Monthly'}
                    </button>
                    <button
                      onClick={() => handleSubscribe(plan.id, 'yearly')}
                      disabled={processingPlan === `${plan.id}-yearly`}
                      className="w-full px-4 py-2 bg-app-surface-hover hover:bg-app-border text-app-text-light dark:text-app-text rounded transition-colors border border-app-border-light dark:border-app-border disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingPlan === `${plan.id}-yearly` ? 'Processing...' : 'Subscribe Yearly (Save 15%)'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Manage Billing */}
      {hasActiveSubscription && subscription?.status !== 'trialing' && (
        <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-6">
          <h2 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-2">
            Manage Subscription
          </h2>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary mb-4">
            Update your payment method, view invoices, change plans, or cancel your subscription.
          </p>
          <button
            onClick={handleManageBilling}
            className="px-6 py-2 bg-app-accent hover:bg-app-accent-hover text-white rounded transition-colors"
          >
            Open Billing Portal
          </button>
        </div>
      )}
    </div>
  );
};

export default DealerBillingScreen;
